import React from "react";
import { CheckCircle2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateOnly } from "../../../utils/datetime";
import { paymentLabel, paymentSubLabel, windowLabel } from "./overviewSelectors";

/**
 * The newest mirrored Bexio payment receipts (A1).
 *
 * A ROW-LEVEL FEED, NOT CASH-FLOW ANALYTICS. It holds no period sum, so it must never
 * be captioned "cash received this month" — that figure lives in the Zahlungseingang
 * KPI, which comes from the cashflow contract.
 *
 * THE PERIOD NARROWS IT AND DOES NOT PROMOTE IT (P3b). With a period selected these
 * are the latest receipts INSIDE that window — the filter and the ordering are both
 * applied server-side, so this is not "the latest five overall, then filtered", which
 * would show nothing whenever the window is not the most recent one. Several payments
 * against one invoice stay several rows: they are several real receipts, and no
 * invoice-level dedup happens here or upstream.
 *
 * Without a window ("Gesamte Zeit") the feed is the latest receipts overall, which is
 * exactly what that selection asks for — so this is the one period-aware card with no
 * unsupported state.
 *
 * EVERY invoice-derived field can be null: `bexio_invoice_payments` has no foreign key
 * to the invoice, and a payment on a general invoice has no tenancy at all. The row is
 * rendered regardless — money that arrived in a bank account does not become less real
 * because a lookup missed — and the label falls back contact → document number →
 * provider id. Never "Unbekannter Mieter": that would report a data gap where the data
 * is simply shaped differently.
 */

const fmtChf = (n) => {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "—";
  return `CHF ${new Intl.NumberFormat("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v)}`;
};

export default function OverviewPaymentsCard({
  payments,
  window: periodWindow,
  loading,
  onRetry,
}) {
  const items = payments?.data?.items;
  const error = payments?.error;
  const label = windowLabel(periodWindow);

  return (
    <section
      aria-labelledby="overview-payments-heading"
      aria-busy={loading || undefined}
      className="rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <h2
        id="overview-payments-heading"
        className="text-[13px] font-semibold text-card-foreground"
      >
        Zahlungseingänge
      </h2>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        {label
          ? `Zuletzt erfasste Zahlungen aus bexio · ${label}`
          : "Zuletzt erfasste Zahlungen aus bexio"}
      </p>

      <div className="mt-3">
        {loading ? (
          <ul className="space-y-3">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-center gap-3">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-20" />
              </li>
            ))}
          </ul>
        ) : error ? (
          <div className="flex flex-wrap items-center gap-2">
            <p role="alert" className="text-[12px] text-destructive">
              Zahlungseingänge konnten nicht geladen werden.
            </p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
              >
                Erneut laden
              </button>
            ) : null}
          </div>
        ) : Array.isArray(items) && items.length > 0 ? (
          <ul className="divide-y divide-border">
            {items.map((p) => {
              const sub = paymentSubLabel(p);
              return (
                <li
                  key={p.payment_id}
                  className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <CheckCircle2
                    aria-hidden
                    className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-medium text-card-foreground">
                      {paymentLabel(p)}
                    </p>
                    {sub ? (
                      <p className="truncate text-[11px] text-muted-foreground">{sub}</p>
                    ) : null}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-[12px] font-semibold tabular-nums text-card-foreground">
                      {fmtChf(p.amount_chf)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {p.payment_date ? formatDateOnly(p.payment_date) : "Ohne Datum"}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-[12px] text-muted-foreground">
            {label
              ? "Keine Zahlungseingänge in diesem Zeitraum"
              : "Keine Zahlungseingänge vorhanden"}
          </p>
        )}
      </div>
    </section>
  );
}
